import { forwardRef } from 'react';

export interface RedRingOfDeathIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
}

/**
 * Red Ring of Death icon - inspired by the Xbox 360 error indicator
 * Used for critical system failures and authentication errors
 * Features animated glow effects and power symbol
 */
export const RedRingOfDeathIcon = forwardRef<SVGSVGElement, RedRingOfDeathIconProps>(({ size = 64, className, ...props }, ref) => {
  // Core colors for the Red Ring of Death
  const redColor = '#dc2626'; // Red-600
  const darkColor = '#6b7280'; // Gray-500
  const powerButtonColor = '#10b981'; // Emerald-500

  return (
    <svg ref={ref} className={className} fill="none" height={size} viewBox="0 0 64 64" width={size} xmlns="http://www.w3.org/2000/svg" {...props}>
      {/* Define the pulsing animation */}
      <defs>
        <filter height="200%" id="glow" width="200%" x="-50%" y="-50%">
          <feGaussianBlur result="blur" stdDeviation="3" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>

        <animate attributeName="opacity" dur="1.5s" id="pulseAnim" repeatCount="indefinite" values="1;0.6;1" />
      </defs>

      {/* Perfect Circular Ring - Four Segments with clear gaps */}
      {/* Circle: r=28, circumference = 2πr ≈ 176, each segment ≈ 40 units with 4 unit gaps */}

      {/* Segment 1: Bottom-Left (Red) - 225° to 315° */}
      <circle
        cx="32"
        cy="32"
        fill="none"
        r="28"
        stroke={redColor}
        strokeDasharray="40 136"
        strokeDashoffset="20"
        strokeLinecap="butt"
        strokeWidth="3.375"
        transform="rotate(225 32 32)"
      >
        <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="1;0.8;1" />
      </circle>

      {/* Segment 2: Bottom-Right (Red) - 315° to 45° */}
      <circle
        cx="32"
        cy="32"
        fill="none"
        r="28"
        stroke={redColor}
        strokeDasharray="40 136"
        strokeDashoffset="20"
        strokeLinecap="butt"
        strokeWidth="3.375"
        transform="rotate(315 32 32)"
      >
        <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="1;0.8;1" />
      </circle>

      {/* Segment 3: Top-Right (Gray/Subdued) - 45° to 135° */}
      <circle
        cx="32"
        cy="32"
        fill="none"
        r="28"
        stroke={darkColor}
        strokeDasharray="40 136"
        strokeDashoffset="20"
        strokeLinecap="butt"
        strokeWidth="3.375"
        transform="rotate(45 32 32)"
      />

      {/* Segment 4: Top-Left (Red) - 135° to 225° */}
      <circle
        cx="32"
        cy="32"
        fill="none"
        r="28"
        stroke={redColor}
        strokeDasharray="40 136"
        strokeDashoffset="20"
        strokeLinecap="butt"
        strokeWidth="3.375"
        transform="rotate(135 32 32)"
      >
        <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="1;0.8;1" />
      </circle>

      {/* Inner Power Symbol - Circle with gap at top (C-shape) */}
      <path d="M 34 30.5 A 4 4 0 1 1 30 30.5" fill="none" stroke={powerButtonColor} strokeLinecap="round" strokeWidth="1.40625">
        <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="1;0.8;1" />
      </path>

      {/* Inner Power Symbol - Vertical Line extending through gap into circle */}
      <line stroke={powerButtonColor} strokeLinecap="round" strokeWidth="1.40625" x1="32" x2="32" y1="26.2" y2="30">
        <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="1;0.8;1" />
      </line>

      {/* Enhanced Glow Effects */}
      <g filter="url(#glow)">
        {/* Perfect Circular Glow Segments */}

        {/* Segment 1 Glow: Bottom-Left (Red) */}
        <circle
          cx="32"
          cy="32"
          fill="none"
          r="28"
          stroke={redColor}
          strokeDasharray="40 136"
          strokeDashoffset="20"
          strokeLinecap="butt"
          strokeOpacity="0.4"
          strokeWidth="5.625"
          transform="rotate(225 32 32)"
        >
          <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="0.4;0.7;0.4" />
        </circle>

        {/* Segment 2 Glow: Bottom-Right (Red) */}
        <circle
          cx="32"
          cy="32"
          fill="none"
          r="28"
          stroke={redColor}
          strokeDasharray="40 136"
          strokeDashoffset="20"
          strokeLinecap="butt"
          strokeOpacity="0.4"
          strokeWidth="5.625"
          transform="rotate(315 32 32)"
        >
          <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="0.4;0.7;0.4" />
        </circle>

        {/* Segment 4 Glow: Top-Left (Red) */}
        <circle
          cx="32"
          cy="32"
          fill="none"
          r="28"
          stroke={redColor}
          strokeDasharray="40 136"
          strokeDashoffset="20"
          strokeLinecap="butt"
          strokeOpacity="0.4"
          strokeWidth="5.625"
          transform="rotate(135 32 32)"
        >
          <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="0.4;0.7;0.4" />
        </circle>

        {/* Power Button Glow - Circle with gap at top (C-shape) */}
        <path
          d="M 34 30.5 A 4 4 0 1 1 30 30.5"
          fill="none"
          filter="blur(3px)"
          stroke={powerButtonColor}
          strokeLinecap="round"
          strokeOpacity="0.5"
          strokeWidth="2.25"
        >
          <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="0.5;0.8;0.5" />
        </path>

        <line filter="blur(3px)" stroke={powerButtonColor} strokeLinecap="round" strokeOpacity="0.5" strokeWidth="2.25" x1="32" x2="32" y1="26.2" y2="30">
          <animate attributeName="stroke-opacity" dur="1.5s" repeatCount="indefinite" values="0.5;0.8;0.5" />
        </line>
      </g>
    </svg>
  );
});

RedRingOfDeathIcon.displayName = 'RedRingOfDeathIcon';
